import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { organizationService } from "@/services/organizationService";
import type {
  OrganizationAddress,
  OrganizationCreatePayload,
  OrganizationUpdatePayload,
} from "@/types/organization";

const keys = {
  list: (wsId: number) => ["organizations", wsId] as const,
  one: (wsId: number, id: number) => ["organizations", wsId, id] as const,
  cert: (wsId: number, id: number) => ["organization-certificate", wsId, id] as const,
};

export function useOrgCertificate(workspaceId: number | null, orgId: number | null) {
  return useQuery({
    queryKey: keys.cert(workspaceId ?? 0, orgId ?? 0),
    queryFn: () => organizationService.getCertificate(workspaceId as number, orgId as number),
    enabled: workspaceId != null && orgId != null,
  });
}

export function useUploadOrgCertificate(workspaceId: number, orgId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, password }: { file: File; password: string }) =>
      organizationService.uploadCertificate(workspaceId, orgId, file, password),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.cert(workspaceId, orgId) }),
  });
}

export function useRevokeOrgCertificate(workspaceId: number, orgId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => organizationService.revokeCertificate(workspaceId, orgId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.cert(workspaceId, orgId) }),
  });
}

export function useOrganizations(workspaceId: number | null) {
  return useQuery({
    queryKey: keys.list(workspaceId ?? 0),
    queryFn: () => organizationService.list(workspaceId as number),
    enabled: workspaceId != null,
  });
}

export function useOrganization(workspaceId: number | null, orgId: number | null) {
  return useQuery({
    queryKey: keys.one(workspaceId ?? 0, orgId ?? 0),
    queryFn: () => organizationService.get(workspaceId as number, orgId as number),
    enabled: workspaceId != null && orgId != null,
  });
}

export function useCreateOrganization(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: OrganizationCreatePayload) =>
      organizationService.create(workspaceId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.list(workspaceId) }),
  });
}

export function useUpdateOrganization(workspaceId: number, orgId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: OrganizationUpdatePayload) =>
      organizationService.update(workspaceId, orgId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list(workspaceId) });
      void qc.invalidateQueries({ queryKey: keys.one(workspaceId, orgId) });
    },
  });
}

export function useSetOrganizationAddress(workspaceId: number, orgId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: OrganizationAddress) =>
      organizationService.setAddress(workspaceId, orgId, payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.one(workspaceId, orgId) }),
  });
}

export function useDeleteOrganization(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgId: number) => organizationService.remove(workspaceId, orgId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.list(workspaceId) }),
  });
}

export { useCnpjLookup } from "@/hooks/useCnpjLookup";
